import { Breadcrumbs, Link, Typography, Box, useTheme } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Home as HomeIcon } from '@mui/icons-material';

const LABELS: Record<string, string> = {
  'master-admin': 'Sistem',
  'dashboard': 'Dashboard',
  'clinic': 'Dashboard',
  'doctor': 'Dashboard',
  'users': 'Utilizatori',
  'clinics': 'Clinici',
  'shelters': 'Adaposturi',
  'vets': 'Veterinari',
  'appointments': 'Programari',
  'payments': 'Plati',
  'subscriptions': 'Abonamente',
  'notifications': 'Notificari',
  'errors': 'Erori',
  'settings': 'Setari Master',
  'reports': 'Rapoarte & Audit',
  'automation': 'Automatizari',
};

// Regex pentru a detecta UUID-uri
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Functie pentru a verifica daca un string este un UUID
const isUUID = (str: string): boolean => {
  return UUID_REGEX.test(str);
};

// Functie pentru a capitaliza un string (prima litera majuscula)
const capitalize = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export default function BreadcrumbsBar() {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const parts = pathname.split('/').filter(Boolean);

  // Filtreaza UUID-urile si pastreaza doar segmentele de meniu
  const menuParts = parts.filter(part => !isUUID(part));

  // Adauga "Acasa" pentru dashboard pages
  const shouldShowHome = menuParts.length > 0 || pathname === '/dashboard' || pathname === '/';

  // Functie pentru a obtine label-ul corect pentru un segment
  const getLabel = (part: string): string => {
    // Schimba "client" in "Client" cu majuscula
    if (part === 'client') {
      return 'Client';
    }
    
    // Foloseste LABELS sau capitalizeaza part-ul
    return LABELS[part] || capitalize(part);
  };

  // Construieste path-urile corecte (fara UUID-uri)
  const buildPath = (index: number): string => {
    // Construim path-ul incluzand UUID-urile pana la segmentul curent din menuParts
    const neededParts: string[] = [];
    let menuIndex = 0;
    
    for (let i = 0; i < parts.length; i++) {
      if (isUUID(parts[i])) {
        // Include UUID-urile care sunt necesare pentru path
        neededParts.push(parts[i]);
      } else {
        // Include segmentele de meniu pana la index-ul curent
        if (menuIndex <= index) {
          neededParts.push(parts[i]);
          menuIndex++;
        } else {
          break;
        }
      }
    }
    
    return '/' + neededParts.join('/');
  };
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Breadcrumbs aria-label="breadcrumb" separator="/">
        {/* Adauga "Acasa" daca este necesar */}
        {shouldShowHome && (
              <Link
                component={RouterLink}
                to="/"
                underline="hover"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                  color: isDark ? '#aaaaaa' : 'text.secondary',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main'
                  }
                }}
              >
                <HomeIcon sx={{ fontSize: '16px' }} />
                <Typography variant="body2" color="inherit" sx={{ fontSize: '14px', fontWeight: 500 }}>
                  Acasa
                </Typography>
              </Link>
        )}
        
        {/* Restul breadcrumb-urilor - doar segmentele de meniu (fara UUID-uri) */}
        {menuParts.map((part, index) => {
          // Construieste path-ul corect includand UUID-urile necesare
          const href = buildPath(index);
          const label = getLabel(part);
          const isLast = index === menuParts.length - 1;
          
          return isLast ? (
            <Typography 
              key={href} 
              color="text.primary" 
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '14px'
              }}
            >
              {label}
            </Typography>
          ) : (
            <Link 
              key={href} 
              component={RouterLink} 
              underline="hover" 
              color="text.secondary"
              to={href}
              variant="body2"
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              {label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}
